import { useEffect, useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, Loader2, Info, Eye, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Broadcast card — WC-only.
 *
 * Flow:
 *   1. Card shows "Send leaderboard update" + entrant count
 *   2. Click → fetches the preview HTML (rendered from the first paid
 *      entrant's perspective, exactly what recipients will see) and opens
 *      a full-screen preview modal
 *   3. From the modal the admin either "Send to N entrants" or "Cancel"
 *   4. Server-side rate-limited to once per UTC day; batch-sent via Resend
 */
export default function BroadcastCard({ slug, primary, authHeaders }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {subject, html, sample_recipient, recipient_count}
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/clubs/${slug}/broadcast-status`, authHeaders);
      setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not check broadcast status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authHeaders) return;
    loadStatus();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = async () => {
    setPreviewLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/clubs/${slug}/broadcast-preview`, authHeaders);
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const r = await axios.post(`${API}/clubs/${slug}/broadcast-leaderboard`, {}, authHeaders);
      setResult(r.data);
      setPreview(null);
      await loadStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (detail?.message || 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  if (loading) return null;

  const canOpen = status && status.can_send && status.recipient_count > 0;
  const count = status?.recipient_count ?? 0;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="broadcast-card">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Leaderboard update email</p>
        {status?.last_sent_at && (
          <p className="text-[11px] text-gray-400">Last sent {new Date(status.last_sent_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
        )}
      </div>
      <p className="text-sm text-gray-700 mb-4">
        Preview and send a personalised leaderboard update to <strong>all {count} paid entrant{count === 1 ? '' : 's'}</strong>. Each recipient sees their current rank + points, the top 10, and a one-click unsubscribe link.
      </p>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-3 flex items-start gap-2" data-testid="broadcast-success">
          <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <strong>Sent {result.sent} of {result.total} emails.</strong>
            {result.failed > 0 && <span className="text-amber-800"> {result.failed} failed — check the Resend dashboard for details.</span>}
            <div className="text-xs text-green-700/80 mt-1">Next update available tomorrow (UTC midnight).</div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3 text-sm text-red-800" data-testid="broadcast-error">{error}</div>
      )}

      {status && !status.can_send && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-3 flex items-start gap-2 text-sm text-amber-900" data-testid="broadcast-cooldown">
          <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <span>You&apos;ve sent today&apos;s update. Next available at UTC midnight.</span>
        </div>
      )}

      {status && count === 0 && status.can_send && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3 text-sm text-gray-600" data-testid="broadcast-no-recipients">
          No paid entrants with an email address yet — the button will light up once your first entries roll in.
        </div>
      )}

      <button
        type="button"
        onClick={openPreview}
        disabled={!canOpen || previewLoading}
        className="w-full py-3 font-bold text-sm rounded-lg text-white transition-opacity flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: primary }}
        data-testid="broadcast-open-preview"
      >
        {previewLoading ? <><Loader2 size={16} className="animate-spin" /> Loading preview…</> : <><Eye size={16} /> Preview leaderboard email</>}
      </button>

      {preview && (
        <PreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onSend={send}
          sending={sending}
          error={error}
          primary={primary}
        />
      )}
    </section>
  );
}

function PreviewModal({ preview, onClose, onSend, sending, error, primary }) {
  const { subject, html, sample_recipient: sample, recipient_count: count } = preview;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 bg-black/60" data-testid="broadcast-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-0.5">Preview · sample recipient</p>
            <p className="text-sm font-semibold text-gray-900">{sample?.player_name} — {sample?.player_email}</p>
            <p className="text-xs text-gray-500 mt-0.5"><strong>Subject:</strong> {subject}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500" aria-label="Close" data-testid="broadcast-preview-close">
            <X size={16} />
          </button>
        </div>

        {/* Preview iframe — using srcDoc so the email HTML renders exactly as it would in an inbox */}
        <div className="flex-1 overflow-hidden bg-gray-50 p-4">
          <iframe
            title="Email preview"
            srcDoc={html}
            className="w-full h-full min-h-[420px] bg-white border border-gray-200 rounded-lg"
            sandbox=""
            data-testid="broadcast-preview-iframe"
          />
        </div>

        {/* Footer / send bar */}
        <div className="px-5 py-4 border-t border-gray-200 bg-white">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 text-sm text-red-800">{error}</div>
          )}
          <p className="text-xs text-gray-600 mb-3">
            Each entrant sees the email personalised to their own rank &amp; points. You can only send this once per day, so double-check the preview above before confirming.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 py-2.5 text-sm font-bold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              data-testid="broadcast-preview-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={sending}
              className="flex-1 py-2.5 text-sm font-bold text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: primary }}
              data-testid="broadcast-preview-send"
            >
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send to {count} {count === 1 ? 'entrant' : 'entrants'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
